// Home landing (July-14 FR-P1/FR-P2): role-appropriate dashboard inside the existing nav shell.
// Admin → admin-dashboard; Central → central-dashboard; AE/Manager → ae-dashboard;
// Student / QC-in-training → student-dashboard. Portal strip stays at the bottom.
import { Link } from "react-router-dom";
import { type Action } from "@rrid/shared-types";
import { useCan } from "../components/Can.js";
import { useAuth } from "../lib/auth.js";
import { Card, CardBody } from "../components/ui.js";
import { AeDashboardView } from "./home/AeDashboard.js";
import { StudentDashboardView } from "./home/StudentDashboard.js";
import { StaffDashboardView } from "./home/StaffDashboard.js";

const PORTALS: { to: string; title: string; emoji: string; action?: Action }[] = [
  { to: "/preprints", title: "Find a Preprint", emoji: "📄" },
  { to: "/reviewers", title: "Find Reviewers", emoji: "🔎" },
  { to: "/quality-check", title: "Quality Check", emoji: "✅" },
  { to: "/operations", title: "Operations", emoji: "📣", action: "openOperations" },
];

export function Home(): JSX.Element {
  const { user } = useAuth();
  const can = useCan();
  const roles = user?.roles ?? [];
  const isAdmin = roles.includes("Admin");
  const isCentral = roles.includes("Central");
  // AE-and-above is exactly `advanceToPitchEditorial` with no domain scope (students & QC-in-training
  // are denied it) — the same tell the AE Details page uses.
  const isAe = can("advanceToPitchEditorial", {});
  const portals = PORTALS.filter((p) => !p.action || can(p.action));

  let dashboard: JSX.Element;
  if (isAdmin) dashboard = <StaffDashboardView variant="Admin" />;
  else if (isCentral) dashboard = <StaffDashboardView variant="Central" />;
  else if (isAe) dashboard = <AeDashboardView />;
  else dashboard = <StudentDashboardView />;

  return (
    <div className="space-y-6">
      {dashboard}

      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-500">Jump to</h3>
        <div className="flex flex-wrap gap-3">
          {portals.map((p) => (
            <Link key={p.to} to={p.to}>
              <Card className="transition hover:border-brand hover:shadow-sm">
                <CardBody className="flex items-center gap-2 py-2">
                  <span className="text-lg">{p.emoji}</span>
                  <span className="text-sm font-medium text-brand">{p.title}</span>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
